export type CommandPaletteMatch = {
  score: number;
  labelIndices: number[];
  breadcrumbIndices: number[][];
};

function isWordChar(char: string): boolean {
  return /[a-z0-9]/i.test(char);
}

function isWordStart(haystack: string, index: number): boolean {
  if (index <= 0) return true;
  return !isWordChar(haystack[index - 1] ?? "");
}

function tokenizeQuery(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Best subsequence for one token. Prefers consecutive runs and word starts. */
export function matchToken(haystackLower: string, token: string): { indices: number[]; score: number } | null {
  if (!token) return { indices: [], score: 0 };

  const consecutiveAt = haystackLower.indexOf(token);
  if (consecutiveAt >= 0) {
    const indices = Array.from({ length: token.length }, (_, i) => consecutiveAt + i);
    return { indices, score: scoreIndices(haystackLower, indices) + 400 };
  }

  const indices: number[] = [];
  let pos = 0;

  for (const char of token) {
    let consecutiveHit = -1;
    if (indices.length > 0) {
      const next = indices[indices.length - 1] + 1;
      if (next < haystackLower.length && haystackLower[next] === char) {
        consecutiveHit = next;
      }
    }

    if (consecutiveHit >= 0) {
      indices.push(consecutiveHit);
      pos = consecutiveHit + 1;
      continue;
    }

    let wordStartHit = -1;
    let anyHit = -1;
    for (let i = pos; i < haystackLower.length; i++) {
      if (haystackLower[i] !== char) continue;
      if (isWordStart(haystackLower, i)) {
        wordStartHit = i;
        break;
      }
      if (anyHit < 0) anyHit = i;
    }

    const found = wordStartHit >= 0 ? wordStartHit : anyHit;
    if (found < 0) return null;
    indices.push(found);
    pos = found + 1;
  }

  return { indices, score: scoreIndices(haystackLower, indices) };
}

function scoreIndices(haystackLower: string, indices: number[]): number {
  if (indices.length === 0) return 0;

  let score = indices.length * 10;
  for (let i = 0; i < indices.length; i++) {
    if (isWordStart(haystackLower, indices[i])) score += 80;
    if (i > 0 && indices[i] === indices[i - 1] + 1) score += 50;
  }

  score += Math.max(0, 50 - indices[0]);
  const span = indices[indices.length - 1] - indices[0] + 1;
  score += Math.max(0, 120 - span);
  return score;
}

function matchAllTokens(haystack: string, tokens: string[]): { indices: number[]; score: number } | null {
  const haystackLower = haystack.toLowerCase();
  const indexSet = new Set<number>();
  let score = 0;

  for (const token of tokens) {
    const matched = matchToken(haystackLower, token);
    if (!matched) return null;
    matched.indices.forEach((index) => indexSet.add(index));
    score += matched.score;
  }

  return { indices: [...indexSet].sort((a, b) => a - b), score };
}

export function matchCommandPaletteQuery(
  query: string,
  label: string,
  breadcrumbs: string[],
  keywords: string[] = []
): CommandPaletteMatch | null {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) {
    return {
      score: 0,
      labelIndices: [],
      breadcrumbIndices: breadcrumbs.map(() => [])
    };
  }

  const scoringHaystack = `${breadcrumbs.join(" ")} ${label} ${keywords.join(" ")}`.trim();
  const scored = matchAllTokens(scoringHaystack, tokens);
  if (!scored) return null;

  const labelMatch = matchAllTokens(label, tokens);
  const score = scored.score + (labelMatch ? 300 : 0);

  return {
    score,
    labelIndices: collectFieldIndices(label, tokens),
    breadcrumbIndices: breadcrumbs.map((crumb) => collectFieldIndices(crumb, tokens))
  };
}

function collectFieldIndices(text: string, tokens: string[]): number[] {
  const lower = text.toLowerCase();
  const indices = new Set<number>();
  for (const token of tokens) {
    const matched = matchToken(lower, token);
    matched?.indices.forEach((index) => indices.add(index));
  }
  return [...indices].sort((a, b) => a - b);
}
